import colors from "assets/styles/colors"
import React, { useRef, useState, useEffect, useContext } from "react"
import styled from "styled-components"
import BGGrid from "components/uIElements/BGGrid"
import gsap from "gsap"
import CustomEase from "gsap/CustomEase"
import text from "assets/styles/text"
import media from "assets/styles/media"
import { LoaderContext, ScreenContext } from "components/Providers"
import { LongPixelButton } from "components/buttons/LongPixelButton"
import { useMedia } from "utils/hooks"

const WholeTime: React.FC<{}> = () => {
  const [wrapper, setWrapper] = useState<HTMLElement | null>(null)
  const graph = useRef<HTMLDivElement>(null)
  const line = useRef<SVGGElement>(null)
  const desktopPlus = useMedia(true, true, false, false)
  const { mobile } = useContext(ScreenContext)

  const setLoad = useContext(LoaderContext).setLoad

  const handleClick = () => {
    setLoad("/who-we-are")
  }

  const gridConfig = {
    sectionHeight: "100%",
    sectionRef: wrapper,
    animationClass: "conviction",
  }

  useEffect(() => {
    const barsArr = document.querySelectorAll(".conviction_bars")
    if (barsArr) {
      gsap.set(barsArr, {
        transformOrigin: "bottom center",
        scaleY: 0,
      })
    }
  }, [])

  useEffect(() => {
    if (desktopPlus) {
      //  sticky scrolling with easing
      if (wrapper)
        gsap.fromTo(wrapper.children, {
          y: 0,
        },
        {
          y: "175vh",
          ease: CustomEase.create("custom", "M0, 0 C0.25, 0 0.75, 1 1, 1 "),
          scrollTrigger: {
            trigger: wrapper,
            scrub: true,
            scroller: "#___gatsby",
            start: "top 35%",
            end: "bottom 90%",
          },
        })

      //graph timeline
      const tl = gsap.timeline({ease:"power1.out", scrollTrigger: {
        trigger: wrapper,
        scroller: "#___gatsby",
        start: `top 20%`,
        end: `bottom 110%`,
        scrub: true,
        // markers: true,
      },
    })

      //slide in bars

      const barsArr = document.querySelectorAll(".conviction_bars")
      tl.to(barsArr, {
        scaleY: 1,
        stagger: 0.1,
        transformOrigin: "bottom center",
      }, 0)

      //crop in line
      tl.fromTo(line.current, {
        clipPath: "polygon(0 0, 0 100%, 100% 100%, 0% 100%)",
      },
      {
        clipPath: "polygon(0 0, 0 100%, 100% 100%, 100% 100%)",
        ease: "power3.in",
        duration: 2,
      }, "0")
      .to(line.current, {
        clipPath: "polygon(0 0, 0 100%, 100% 100%, 100% 0%)",
        ease: "power3.out",
        duration: 2,
      }, ">")

      return () => {
        tl.kill()
      }
    } else {
      //graph timeline
      const tl = gsap.timeline({ease:"power1.out", scrollTrigger: {
        trigger: graph.current,
        scroller: "#___gatsby",
        start: mobile ? "top 80%" : "top 50%",
        end: mobile ? "bottom 40%" : "bottom 40%",
        // markers: true,
        scrub: true,
      },
    })

      //slide in bars
      const barsArr = document.querySelectorAll(".conviction_bars")
      tl.to(barsArr, {
        scaleY: 1,
        stagger: 0.1,
        transformOrigin: "bottom center",
      }, 0)
      //crop in line
      if (line.current)
        tl.fromTo(line.current, {
          clipPath: "polygon(0 0, 0 100%, 100% 100%, 0% 100%)",
        },
        {
          clipPath: "polygon(0 0, 0 100%, 100% 100%, 100% 100%)",
          ease: "power3.in",
          duration: 2,
        }, "<+30%")
        .to(line.current, {
          clipPath: "polygon(0 0, 0 100%, 100% 100%, 100% 0%)",
          ease: "power3.out",
          duration: 2,
        }, ">")

      return () => {
        tl.kill()
      }
    }
  }, [wrapper, desktopPlus])

  return (
    <Wrapper
      id="Conviction"
      ref={ref => setWrapper(ref)}
      data-scroll
      data-scroll-section
    >
      <BGGrid lineConfig={gridConfig} />
      <ContentWrapper>
        <Graph ref={graph}>
          <Svg
            fill="none"
            viewBox="0 0 545 545"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path fill="#C4C4C4" d="M0 504h545v1H0z" />
            <path
              d="M21.925 529v-1.283h.641v-.642h.646v-.646h.642v-.641h.642v.641h.646v.646h.641v.642h.642V529h1.287v-1.925h-.646v-.646h-.641v-.641h-.642v-.642h-.641v-1.288h.641v-.641h.642v-.642h.641v-.646h.646V520h-1.287v1.288h-.642v.641h-.641v.646h-.646v.642h-.642v-.642h-.642v-.646h-.646v-.641h-.641V520h-1.283v1.929h.641v.646h.642v.642h.641v.641h.646v1.288h-.646v.642h-.641v.641h-.642v.646h-.641V529h1.283ZM70.925 529v-1.283h.641v-.642h.646v-.646h.642v-.641h.642v.641h.646v.646h.641v.642h.642V529h1.287v-1.925h-.646v-.646h-.641v-.641h-.642v-.642h-.641v-1.288h.641v-.641h.642v-.642h.641v-.646h.646V520h-1.287v1.288h-.642v.641h-.641v.646h-.646v.642h-.642v-.642h-.642v-.646h-.646v-.641h-.641V520h-1.283v1.929h.641v.646h.642v.642h.641v.641h.646v1.288h-.646v.642h-.641v.641h-.642v.646h-.641V529h1.283ZM119.925 529v-1.283h.641v-.642h.646v-.646h.642v-.641h.642v.641h.646v.646h.641v.642h.642V529h1.287v-1.925h-.646v-.646h-.641v-.641h-.642v-.642h-.641v-1.288h.641v-.641h.642v-.642h.641v-.646h.646V520h-1.287v1.288h-.642v.641h-.641v.646h-.646v.642h-.642v-.642h-.642v-.646h-.646v-.641h-.641V520h-1.283v1.929h.641v.646h.642v.642h.641v.641h.646v1.288h-.646v.642h-.641v.641h-.642v.646h-.641V529h1.283ZM168.925 529v-1.283h.641v-.642h.646v-.646h.642v-.641h.642v.641h.646v.646h.641v.642h.642V529h1.287v-1.925h-.646v-.646h-.641v-.641h-.642v-.642h-.641v-1.288h.641v-.641h.642v-.642h.641v-.646h.646V520h-1.287v1.288h-.642v.641h-.641v.646h-.646v.642h-.642v-.642h-.642v-.646h-.646v-.641h-.641V520h-1.283v1.929h.641v.646h.642v.642h.641v.641h.646v1.288h-.646v.642h-.641v.641h-.642v.646h-.641V529h1.283ZM217.925 529v-1.283h.641v-.642h.646v-.646h.642v-.641h.642v.641h.646v.646h.641v.642h.642V529h1.287v-1.925h-.646v-.646h-.641v-.641h-.642v-.642h-.641v-1.288h.641v-.641h.642v-.642h.641v-.646h.646V520h-1.287v1.288h-.642v.641h-.641v.646h-.646v.642h-.642v-.642h-.642v-.646h-.646v-.641h-.641V520h-1.283v1.929h.641v.646h.642v.642h.641v.641h.646v1.288h-.646v.642h-.641v.641h-.642v.646h-.641V529h1.283ZM266.925 529v-1.283h.641v-.642h.646v-.646h.642v-.641h.642v.641h.646v.646h.641v.642h.642V529h1.287v-1.925h-.646v-.646h-.641v-.641h-.642v-.642h-.641v-1.288h.641v-.641h.642v-.642h.641v-.646h.646V520h-1.287v1.288h-.642v.641h-.641v.646h-.646v.642h-.642v-.642h-.642v-.646h-.646v-.641h-.641V520h-1.283v1.929h.641v.646h.642v.642h.641v.641h.646v1.288h-.646v.642h-.641v.641h-.642v.646h-.641V529h1.283ZM315.925 529v-1.283h.641v-.642h.646v-.646h.642v-.641h.642v.641h.646v.646h.641v.642h.642V529h1.287v-1.925h-.646v-.646h-.641v-.641h-.642v-.642h-.641v-1.288h.641v-.641h.642v-.642h.641v-.646h.646V520h-1.287v1.288h-.642v.641h-.641v.646h-.646v.642h-.642v-.642h-.642v-.646h-.646v-.641h-.641V520h-1.283v1.929h.641v.646h.642v.642h.641v.641h.646v1.288h-.646v.642h-.641v.641h-.642v.646h-.641V529h1.283ZM364.925 529v-1.283h.641v-.642h.646v-.646h.642v-.641h.642v.641h.646v.646h.641v.642h.642V529h1.287v-1.925h-.646v-.646h-.641v-.641h-.642v-.642h-.641v-1.288h.641v-.641h.642v-.642h.641v-.646h.646V520h-1.287v1.288h-.642v.641h-.641v.646h-.646v.642h-.642v-.642h-.642v-.646h-.646v-.641h-.641V520h-1.283v1.929h.641v.646h.642v.642h.641v.641h.646v1.288h-.646v.642h-.641v.641h-.642v.646h-.641V529h1.283ZM413.925 529v-1.283h.641v-.642h.646v-.646h.642v-.641h.642v.641h.646v.646h.641v.642h.642V529h1.287v-1.925h-.646v-.646h-.641v-.641h-.642v-.642h-.641v-1.288h.641v-.641h.642v-.642h.641v-.646h.646V520h-1.287v1.288h-.642v.641h-.641v.646h-.646v.642h-.642v-.642h-.642v-.646h-.646v-.641h-.641V520h-1.283v1.929h.641v.646h.642v.642h.641v.641h.646v1.288h-.646v.642h-.641v.641h-.642v.646h-.641V529h1.283ZM462.925 529v-1.283h.641v-.642h.646v-.646h.642v-.641h.642v.641h.646v.646h.641v.642h.642V529h1.287v-1.925h-.646v-.646h-.641v-.641h-.642v-.642h-.641v-1.288h.641v-.641h.642v-.642h.641v-.646h.646V520h-1.287v1.288h-.642v.641h-.641v.646h-.646v.642h-.642v-.642h-.642v-.646h-.646v-.641h-.641V520h-1.283v1.929h.641v.646h.642v.642h.641v.641h.646v1.288h-.646v.642h-.641v.641h-.642v.646h-.641V529h1.283ZM511.925 529v-1.283h.641v-.642h.646v-.646h.642v-.641h.642v.641h.646v.646h.641v.642h.642V529h1.287v-1.925h-.646v-.646h-.641v-.641h-.642v-.642h-.641v-1.288h.641v-.641h.642v-.642h.641v-.646h.646V520h-1.287v1.288h-.642v.641h-.641v.646h-.646v.642h-.642v-.642h-.642v-.646h-.646v-.641h-.641V520h-1.283v1.929h.641v.646h.642v.642h.641v.641h.646v1.288h-.646v.642h-.641v.641h-.642v.646h-.641V529h1.283ZM9 449h30v40H9z"
              fill="#fff"
            />
            <path className="conviction_bars" fill="#fff" d="M9 449h30v40H9z" />

            <path
              className="conviction_bars"
              fill="#fff"
              d="M58 419h30v70H58z"
            />

            <path
              className="conviction_bars"
              fill="#fff"
              d="M107 379h30v110h-30z"
            />

            <path
              className="conviction_bars"
              fill="#fff"
              d="M156 339h30v150h-30z"
            />

            <path
              className="conviction_bars"
              fill="#fff"
              d="M205 359h30v130h-30z"
            />

            <path
              className="conviction_bars"
              fill="#fff"
              d="M254 319h30v170h-30z"
            />

            <path
              className="conviction_bars"
              fill="#fff"
              d="M303 259h30v230h-30z"
            />

            <path
              className="conviction_bars"
              fill="#fff"
              d="M352 169h30v320h-30z"
            />

            <path
              className="conviction_bars"
              fill="#fff"
              d="M401 89h30v400h-30z"
            />

            <path
              className="conviction_bars"
              fill="#fff"
              d="M450 39h30v450h-30z"
            />

            <path
              className="conviction_bars"
              fill="#fff"
              d="M499 0h30v489h-30z"
            />

            <g ref={line}>
              <g clipPath="url(#l)">
                <path
                  id="graph-line"
                  d="M24.5 471C514 471 514 471 514 19"
                  stroke="url(#m)"
                  strokeWidth="5"
                />
              </g>
            </g>
            <defs>
              <linearGradient
                id="m"
                x1="269.25"
                y1="19"
                x2="269.25"
                y2="471"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#1965FE" />
                <stop offset="1" stopColor="#0D3482" />
              </linearGradient>
              <clipPath id="l">
                <path
                  fill="#fff"
                  transform="translate(24.5 19)"
                  d="M0 0h490v452H0z"
                />
              </clipPath>
            </defs>
          </Svg>
        </Graph>
        <div>
          <Title>
            We invest early, <span>with conviction</span>
          </Title>

          <Text>
            We embrace the weird, the boring, the bold, and the different on the
            digital frontier no matter the industry.
          </Text>
          <LongPixelButton
            color={colors.white}
            textColor={colors.black}
            pixelWidth={35}
            activeColor={colors.black50}
            onClick={handleClick}
            pixelsToChange={[
              { from: [0, 32], to: [-2, 33] },
              { from: [0, 33], to: [-2, 35] },
              { from: [1, 34], to: [-1, 34] },
              { from: [9, 0], to: [11, -2] },
              { from: [8, 1], to: [10, -1] },
              { from: [9, 2], to: [11, 1] },
              //pixels that dont move top right
              { from: [0, 34], to: [0, 34] },
              { from: [1, 33], to: [1, 33] },
              { from: [1, 32], to: [1, 32] },
              //pixels that dont move bottom left
              { from: [8, 0], to: [8, 0] },
              { from: [9, 1], to: [9, 1] },
              { from: [8, 2], to: [8, 2] },
            ]}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <p>LEARN MORE</p>
              <svg
                className="pixel-child"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M14.5 8.49998L14.5 6.49998L12.5 6.49998L12.5 8.49998L14.5 8.49998ZM12.5 6.49998L12.5 4.49997L10.5 4.49997L10.5 2.5L8.5 2.5L8.5 0.5L6.5 0.5L6.5 2.5L8.5 2.5L8.5 4.5L10.5 4.5L10.5 6.49998L12.5 6.49998ZM12.5 8.5L12.5 10.5L10.5 10.5L10.5 8.5L12.5 8.5ZM4.5 8.5L6.5 8.5L8.5 8.5L10.5 8.5L10.5 6.5L8.5 6.5L6.5 6.5L4.5 6.5L2.5 6.5L0.5 6.5L0.5 8.5L2.5 8.5L4.5 8.5ZM8.5 12.5L8.5 10.5L10.5 10.5L10.5 12.5L8.5 12.5ZM8.5 12.5L8.5 14.5L6.5 14.5L6.5 12.5L8.5 12.5Z"
                  fill={"black"}
                />
              </svg>
            </div>
          </LongPixelButton>
        </div>
      </ContentWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  min-height: calc(275vh + 12.89vw);
  background: ${colors.blue400};
  padding: 0 11.111vw 0 3.472vw;

  ${media.tablet} {
    min-height: auto;
    height: 133.398vw;
    display: flex;
    flex-direction: column-reverse;
    padding: 0 16.8vw 14.648vw 4.883vw;
  }
  ${media.mobile} {
    padding: 20vw 4vw 26.67vw;
    display: flex;
    flex-direction: column-reverse;
    min-height: auto;
  }
`

const Svg = styled.svg`
  max-width: 100%;
  max-height: 

  path:not(url(#graph-line)) {
    transform: translateY(0);
  }
`

const Title = styled.div`
  ${text.desktop.h4};
  width: 35.97vw;
  white-space: pre-wrap;
  color: ${colors.white};
  span {
    ${text.desktop.d3Point5};
  }

  ${media.tablet} {
    width: 100%;
    ${text.tablet.h3};
    span {
      ${text.tablet.d2Point5};
    }
  }
  ${media.mobile} {
    width: 92vw;

    ${text.mobile.h4};
    span {
      font-size: 24vw;
      display: inline;
    }
  }
`

const Graph = styled.div`
  width: 37.85vw;
  height: 37.85vw;
  display: grid;
  place-items: center;

  ${media.tablet} {
    width: 66.5vw;
    height: 66.5vw;
  }
  ${media.mobile} {
    width: 78.667vw;
    height: 78.667vw;
  }
`
const Text = styled.p`
  ${text.desktop.h5};
  width: 36.18vw;
  margin: 2.78vw 0 4.17vw;
  color: ${colors.blue50};
  ${media.tablet} {
    ${text.tablet.h5};
    margin: 2.44vw 0 4.88vw 0;
    width: 46.39vw;
  }
  ${media.mobile} {
    margin: 6.667vw 0 13.333vw 0;
    font-size: 5.6vw;
    width: auto;
  }
`

const ContentWrapper = styled.div`
  min-height: 85vh;
  margin-top: 15vh;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9.583vw;
  ${media.tablet} {
    flex-direction: column-reverse;
  }
  ${media.mobile} {
    flex-direction: column-reverse;
  }
`
export default WholeTime
